import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import {
  OperationStatus,
  operationStatusCodes,
  sortStatusOptions,
  defaultSatusOrder,
} from 'src/api/wa/store/models/workplace/OperationStatus';
import { FilterStore, useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { UserState, useWaUserStore } from 'src/api/wa/store/WaUserStore';
import DropdownSelect, { OptionProps } from 'src/ui/wards/common/select/DropdownSelect';
import Button from 'src/ui/wards/common/button/Button';
import Input from 'src/ui/wards/common/input/Input';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import { FormRow } from 'src/ui/wards/common/form/FormRow';

type SearchHeaderProps = {
  showListButton: boolean;
  showMapButton: boolean;
  onSearch: () => void;
};

type WAUser = UserState['user'];

const headerButtonClassName = `
  mt-2 w-fit whitespace-nowrap rounded-md border border-gray-400
  bg-gradient-to-b from-gray-100 to-gray-300 p-1 text-gray-800
`;
const searchButtonClassName = `
  ${headerButtonClassName} 
  disabled:cursor-not-allowed disabled:border-gray-300 
  disabled:from-gray-200 disabled:to-gray-200 
  disabled:text-gray-400 disabled:shadow-none
`;

const useInitializeFilterDefaults = (
  workplaceBranchCode: string | undefined,
  user: WAUser,
  filtersInitialized: boolean,
  patchWorkplaceFilterParameters: FilterStore['patchWorkplaceFilterParameters'],
  setCustomerBranchCode: FilterStore['setCustomerBranchCode'],
  setFiltersInitialized: FilterStore['setFiltersInitialized']
) => {
  useEffect(() => {
    if (workplaceBranchCode || !user?.branchCode || filtersInitialized) return;

    patchWorkplaceFilterParameters({
      reserved: false,
      branchCode: user.branchCode,
      salesOfficeCode: user.salesOfficeCode,
      sectionCode: user.sectionCode,
      squadCode: user.squadCode,
      statusList: OperationStatus.Incomplete,
      customerId: '',
    });
    setCustomerBranchCode(user.branchCode);

    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    patchWorkplaceFilterParameters,
    setCustomerBranchCode,
    setFiltersInitialized,
    user,
    workplaceBranchCode,
  ]);
};

const useFetchBranchOptionsEffect = (
  fetchBranchOptions: ReturnType<typeof useWaWorkplaceService>['fetchBranchOptions']
) => {
  useEffect(() => {
    fetchBranchOptions();
  }, [fetchBranchOptions]);
};

const useFetchSalesOfficeOptionsEffect = (
  branchCode: string | undefined,
  fetchSalesOfficeOptions: ReturnType<typeof useWaWorkplaceService>['fetchSalesOfficeOptions']
) => {
  useEffect(() => {
    if (!branchCode) return;
    fetchSalesOfficeOptions(branchCode);
  }, [fetchSalesOfficeOptions, branchCode]);
};

const useFetchSectionOptionsEffect = (
  branchCode: string | undefined,
  salesOfficeCode: string | undefined,
  fetchSectionOptions: ReturnType<typeof useWaWorkplaceService>['fetchSectionOptions'],
  setSectionOptions: FilterStore['setSectionOptions'],
  setSquadOptions: FilterStore['setSquadOptions']
) => {
  useEffect(() => {
    if (!branchCode || !salesOfficeCode) {
      setSectionOptions([]);
      setSquadOptions([]);
      return;
    }

    fetchSectionOptions(branchCode, salesOfficeCode);
  }, [branchCode, fetchSectionOptions, salesOfficeCode, setSectionOptions, setSquadOptions]);
};

const useFetchSquadOptionsEffect = (
  branchCode: string | undefined,
  salesOfficeCode: string | undefined,
  sectionCode: string | undefined,
  fetchSquadOptions: ReturnType<typeof useWaWorkplaceService>['fetchSquadOptions'],
  setSquadOptions: FilterStore['setSquadOptions']
) => {
  useEffect(() => {
    if (!branchCode || !salesOfficeCode || !sectionCode) {
      setSquadOptions([]);
      return;
    }

    fetchSquadOptions(branchCode, salesOfficeCode, sectionCode);
  }, [branchCode, fetchSquadOptions, salesOfficeCode, sectionCode, setSquadOptions]);
};

const useFetchStatusOptionsEffect = (
  fetchStatusOptions: ReturnType<typeof useWaWorkplaceService>['fetchStatusOptions']
) => {
  useEffect(() => {
    fetchStatusOptions();
  }, [fetchStatusOptions]);
};

const buildDropdownOptions = <T extends { name: string; code: string }>(
  items: T[]
): OptionProps[] =>
  items.map((item) => ({
    title: `${item.code} ${item.name}`,
    value: item.code,
  }));

const P_SearchHeader = ({ showListButton, showMapButton, onSearch }: SearchHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    fetchBranchOptions,
    fetchStatusOptions,
    fetchSalesOfficeOptions,
    fetchSectionOptions,
    fetchSquadOptions,
  } = useWaWorkplaceService();
  const {
    branchOptions,
    salesOfficeOptions,
    sectionOptions,
    squadOptions,
    workplaceFilterParameters,
    patchWorkplaceFilterParameters,
    setSalesOfficeOptions,
    setSectionOptions,
    setSquadOptions,
    statusOptions,
    customerBranchCode,
    setCustomerBranchCode,
    setUserOfficeLocation,
    setMapCenter,
    filtersInitialized,
    setFiltersInitialized,
  } = useWaFilterStore();
  const [showMissingLocalOffice, setShowMissingLocalOffice] = useState(false);
  const [showMissingCustomerNumber, setShowMissingCustomerNumber] = useState(false);
  const [showMissingBranch, setShowMissingBranch] = useState(false);
  const [showMissingSecondBranch, setShowMissingSecondBranch] = useState(false);
  const { user } = useWaUserStore();

  useInitializeFilterDefaults(
    workplaceFilterParameters.branchCode,
    user,
    filtersInitialized,
    patchWorkplaceFilterParameters,
    setCustomerBranchCode,
    setFiltersInitialized
  );
  useFetchBranchOptionsEffect(fetchBranchOptions);
  useFetchSalesOfficeOptionsEffect(workplaceFilterParameters.branchCode, fetchSalesOfficeOptions);
  useFetchSectionOptionsEffect(
    workplaceFilterParameters.branchCode,
    workplaceFilterParameters.salesOfficeCode,
    fetchSectionOptions,
    setSectionOptions,
    setSquadOptions
  );
  useFetchSquadOptionsEffect(
    workplaceFilterParameters.branchCode,
    workplaceFilterParameters.salesOfficeCode,
    workplaceFilterParameters.sectionCode,
    fetchSquadOptions,
    setSquadOptions
  );
  useFetchStatusOptionsEffect(fetchStatusOptions);

  const reservedOptions: OptionProps[] = [
    { title: t('wards.common.reservationStatus.all'), value: '' },
    { title: t('wards.common.reservationStatus.reserved'), value: 'true' },
    { title: t('wards.common.reservationStatus.unreserved'), value: 'false' },
  ];

  const initialOptionTranslation = t('wards.common.none');

  const branchDropdownOptions: OptionProps[] = [
    { title: t('wards.common.none'), value: '' },
    ...buildDropdownOptions(branchOptions),
  ];

  const salesOfficeDropdownOptions = buildDropdownOptions(salesOfficeOptions);
  const sectionDropdownOptions = buildDropdownOptions(sectionOptions);
  const squadDropdownOptions = squadOptions.map((item) => ({
    title: item.code,
    value: item.code,
  }));

  const handleRegionalOfficeChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const branchCode = event.target.value;
    patchWorkplaceFilterParameters({
      branchCode,
      salesOfficeCode: '',
      sectionCode: '',
      squadCode: '',
    });
    setCustomerBranchCode(branchCode);
    setShowMissingCustomerNumber(false);
    setShowMissingBranch(false);
    setShowMissingSecondBranch(false);

    if (branchCode) {
      fetchSalesOfficeOptions(branchCode);
    } else {
      setSalesOfficeOptions([]);
      setSectionOptions([]);
      setSquadOptions([]);
    }
  };

  const handleCustomerRegionalOfficeChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const branchCode = event.target.value;
    setCustomerBranchCode(branchCode);
    patchWorkplaceFilterParameters({
      branchCode,
      salesOfficeCode: '',
      sectionCode: '',
      squadCode: '',
      customerId: undefined,
    });
    setShowMissingLocalOffice(false);
    setShowMissingBranch(false);
    setShowMissingSecondBranch(false);
  };

  const handleLocalOfficeChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const salesOfficeCode = event.target.value;
    if (salesOfficeCode !== '') {
      setShowMissingLocalOffice(false);
      setShowMissingCustomerNumber(false);
    }
    patchWorkplaceFilterParameters({
      salesOfficeCode,
      sectionCode: '',
      squadCode: '',
    });

    if (salesOfficeCode && workplaceFilterParameters.branchCode) {
      fetchSectionOptions(workplaceFilterParameters.branchCode, salesOfficeCode);
    } else {
      setSectionOptions([]);
      setSquadOptions([]);
    }
  };

  const handleSectionChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const sectionCode = event.target.value;
    patchWorkplaceFilterParameters({
      sectionCode,
      squadCode: '',
    });

    if (
      sectionCode &&
      workplaceFilterParameters.branchCode &&
      workplaceFilterParameters.salesOfficeCode
    ) {
      fetchSquadOptions(
        workplaceFilterParameters.branchCode,
        workplaceFilterParameters.salesOfficeCode,
        sectionCode
      );
    } else {
      setSquadOptions([]);
    }
  };

  const handleSquadChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    patchWorkplaceFilterParameters({
      squadCode: event.target.value,
    });
  };

  const handleReservationStatusChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    patchWorkplaceFilterParameters({
      reserved: value === '' ? undefined : value === 'true',
    });
  };

  const handleOpenUnaddressedList = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.UnadressedWorkplacesList].path, true);
  };

  const handleOpenUnaddressedMap = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.WorkplacesMap].path, true);
  };

  const handleBuyersNumberChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const hasValue = value.length > 0;

    if (hasValue) {
      setShowMissingCustomerNumber(false);
      setShowMissingLocalOffice(false);
    }

    patchWorkplaceFilterParameters({
      customerId: hasValue ? value : undefined,
    });
  };

  const handleSearch = () => {
    const currentGlobalParams = workplaceFilterParameters;

    const hasRegional = !!currentGlobalParams.branchCode;
    setShowMissingBranch(!hasRegional);

    const hasBranch = !!customerBranchCode;
    setShowMissingSecondBranch(!hasBranch);

    const hasCustomer =
      !!currentGlobalParams.customerId && currentGlobalParams.customerId.trim().length > 0;
    const hasLocal = !!currentGlobalParams.salesOfficeCode;
    if (!hasCustomer && !hasLocal) {
      setShowMissingCustomerNumber(!hasCustomer);
      setShowMissingLocalOffice(!hasLocal);
    }

    if ((!hasCustomer && !hasLocal) || !hasBranch || !hasRegional) return;

    onSearch();
  };

  const getStatusTranslation = (key: string): string => {
    const translationKey = `wards.common.statusCodes.${key}`;
    const translatedValue = t(translationKey);
    return translatedValue === translationKey ? key : translatedValue;
  };

  const customStatusOptions = [
    {
      groupName: OperationStatus.Incomplete,
      statuses: operationStatusCodes(OperationStatus.Incomplete),
    },
    { groupName: OperationStatus.None, statuses: operationStatusCodes(OperationStatus.None) },
  ];

  const fullStatusOptions = [...statusOptions, ...customStatusOptions];

  const statusDropdownOptions: OptionProps[] = sortStatusOptions(
    fullStatusOptions,
    'groupName',
    defaultSatusOrder
  ).map((group) => ({
    title: getStatusTranslation(group.groupName),
    value: group.groupName,
  }));

  const selectedStatusGroup = fullStatusOptions.find(
    (group) => group.groupName === workplaceFilterParameters.statusList
  );

  const handleStateChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedGroupName = event.target.value;

    if (!selectedGroupName) {
      patchWorkplaceFilterParameters({ statusList: OperationStatus.Incomplete });
      return;
    }

    const selectedGroup = fullStatusOptions.find((group) => group.groupName === selectedGroupName);

    patchWorkplaceFilterParameters({
      statusList: selectedGroup?.groupName,
    });
  };

  const selectedBranchOption = branchDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.branchCode
  );
  const selectedSalesOfficeOption = salesOfficeDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.salesOfficeCode
  );
  const selectedSectionOption = sectionDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.sectionCode
  );
  const selectedSquadOption = squadDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.squadCode
  );
  const selectedReservedOption = reservedOptions.find(
    (opt) =>
      opt.value ===
      (workplaceFilterParameters.reserved === undefined
        ? ''
        : String(workplaceFilterParameters.reserved))
  );

  useEffect(() => {
    // Decide office location on map. Office location is stored in Sales Office or Section.
    const salesOffice = salesOfficeOptions.find(
      (office) => office.code === workplaceFilterParameters.salesOfficeCode
    );
    const section = sectionOptions.find(
      (section) => section.code === workplaceFilterParameters.sectionCode
    );
    const officeLocation = section?.location ?? salesOffice?.location ?? null;
    setUserOfficeLocation(officeLocation);

    // In PC screen, when searched with organization, office location is the center of the map.
    setMapCenter(officeLocation);
  }, [
    salesOfficeOptions,
    sectionOptions,
    setUserOfficeLocation,
    setMapCenter,
    workplaceFilterParameters.salesOfficeCode,
    workplaceFilterParameters.sectionCode,
  ]);

  return (
    <div className="flex flex-col gap-y-4 rounded-md border-t-2 border-sky-600 bg-gray-100 p-3 min-[1150px]:flex-row">
      <div className="flex-1 space-y-4 min-[1150px]:pr-4">
        {/* --- FE, Location Search ---  */}
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 min-[850px]:grid-cols-2 min-[1460px]:grid-cols-3">
          <div className="col-span-full font-semibold">
            {t('wards.desktop.unaddressedPropertiesMap.locationSearch')}
          </div>

          <FormRow
            title={t('wards.common.branchOffice')}
            required
            showError={showMissingBranch}
            errorMessage={t('wards.sites.missingBranchOffice')}
          >
            <DropdownSelect
              selectedValue={
                selectedBranchOption ?? { title: t('wards.mapFilter.branchPlaceholder'), value: '' }
              }
              optionValues={branchDropdownOptions}
              onChange={handleRegionalOfficeChanged}
            />
          </FormRow>

          <FormRow
            title={t('wards.common.salesOffice')}
            required
            showError={showMissingLocalOffice}
            errorMessage={t('wards.sites.missingSalesOffice')}
          >
            <DropdownSelect
              placeholder={t('wards.mapFilter.businessPlaceholder')}
              selectedValue={
                selectedSalesOfficeOption ?? {
                  title: t('wards.mapFilter.businessPlaceholder'),
                  value: '',
                }
              }
              optionValues={salesOfficeDropdownOptions}
              onChange={handleLocalOfficeChanged}
            />
          </FormRow>

          <FormRow title={t('wards.common.section')}>
            <DropdownSelect
              placeholder={t('wards.mapFilter.sectionPlaceholder')}
              selectedValue={
                selectedSectionOption ?? {
                  title: t('wards.mapFilter.sectionPlaceholder'),
                  value: '',
                }
              }
              optionValues={sectionDropdownOptions}
              onChange={handleSectionChanged}
            />
          </FormRow>

          <FormRow title={t('wards.common.squad')}>
            <DropdownSelect
              placeholder={t('wards.mapFilter.squadPlaceholder')}
              selectedValue={
                selectedSquadOption ?? { title: t('wards.mapFilter.squadPlaceholder'), value: '' }
              }
              optionValues={squadDropdownOptions}
              onChange={handleSquadChanged}
            />
          </FormRow>

          <FormRow title={t('wards.desktop.unaddressedPropertiesMap.reservationStatus')}>
            <DropdownSelect
              selectedValue={selectedReservedOption}
              optionValues={reservedOptions}
              onChange={handleReservationStatusChanged}
            />
          </FormRow>

          <FormRow title={t('wards.common.status')}>
            <DropdownSelect
              selectedValue={
                selectedStatusGroup
                  ? {
                      title: getStatusTranslation(selectedStatusGroup.groupName),
                      value: selectedStatusGroup.groupName,
                    }
                  : { title: t('wards.common.none'), value: '' }
              }
              optionValues={statusDropdownOptions}
              onChange={handleStateChanged}
            />
          </FormRow>
        </div>

        <div role="separator" aria-orientation="horizontal" className="h-px w-full bg-gray-400" />

        {/* --- Customer Search --- */}
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 min-[850px]:grid-cols-2 min-[1460px]:grid-cols-3">
          <div className="col-span-full font-semibold">
            {t('wards.desktop.unaddressedPropertiesList.clientSearch')}
          </div>

          <FormRow
            title={t('wards.common.branchOffice')}
            required
            showError={showMissingSecondBranch}
            errorMessage={t('wards.sites.missingBranchOffice')}
          >
            <DropdownSelect
              selectedValue={
                branchDropdownOptions.find((opt) => opt.value === customerBranchCode) ?? {
                  title: initialOptionTranslation,
                  value: '',
                }
              }
              optionValues={branchDropdownOptions}
              onChange={handleCustomerRegionalOfficeChanged}
            />
          </FormRow>

          <FormRow
            title={t('wards.common.customerNumber')}
            required
            showError={showMissingCustomerNumber}
            errorMessage={t('wards.sites.missingCustomerNumber')}
          >
            <Input
              onChange={handleBuyersNumberChanged}
              value={workplaceFilterParameters.customerId || ''}
            />
          </FormRow>
        </div>
      </div>

      <div className="flex min-w-[200px] shrink-0 items-center border-t border-gray-300 px-3 pt-4 min-[1150px]:border-l-2 min-[1150px]:border-t-0 min-[1150px]:pt-0">
        <div className="flex w-full flex-col items-start gap-2">
          <Button
            title={t('wards.desktop.unaddressedPropertiesMap.search')}
            className={`${searchButtonClassName} `}
            onClick={handleSearch}
          />
          {showListButton && (
            <Button
              title={t('wards.desktop.unaddressedPropertiesMap.listOfUnresolvedLocations')}
              className={`${headerButtonClassName}`}
              onClick={handleOpenUnaddressedList}
            />
          )}
          {showMapButton && (
            <Button
              title={t('wards.desktop.unaddressedPropertiesMap.mapOfUnresolvedLocations')}
              className={`${headerButtonClassName}`}
              onClick={handleOpenUnaddressedMap}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default P_SearchHeader;
